import React, { useEffect, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CalendarDays,
  Clock3,
  History,
  Laptop,
  LogOut,
  Edit2,
  KeyRound,
  X,
  User,
  Users,
} from "lucide-react";
import {
  fetchUserDetails,
  fetchUserLoginHistory,
  fetchUserOnlineStatus,
  fetchUserSessions,
  forceLogoutUser,
} from "../apis/users";
import { displayToast } from "../utils/displayToast";
import "../styles/components/UserDetails.css";
import "../styles/components/PageStyles.css";

const tabs = [
  { id: "overview", label: "Overview" },
  { id: "sessions", label: "Current Sessions" },
  { id: "login", label: "Login History" },
  { id: "transactions", label: "Transaction Activity" },
];

const getInitials = (name = "") =>
  name
    .split(/[\s_.-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "??";

const formatDate = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
};

const formatLastLogin = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const today = new Date();
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const todayOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const dayDifference = Math.round((todayOnly - dateOnly) / 86400000);
  const time = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (dayDifference === 0) return `Today, ${time}`;
  if (dayDifference === 1) return `Yesterday, ${time}`;
  return `${date.toLocaleDateString([], { day: "numeric", month: "short" })}, ${time}`;
};

const getList = (user, keys) =>
  keys.reduce(
    (items, key) => items || (Array.isArray(user?.[key]) ? user[key] : null),
    null,
  ) || [];

const getPaginationItems = (pageCount, currentPage) => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const visiblePages = new Set([
    1,
    2,
    3,
    currentPage - 1,
    currentPage,
    currentPage + 1,
    pageCount - 2,
    pageCount - 1,
    pageCount,
  ]);
  const pages = [...visiblePages]
    .filter((page) => page >= 1 && page <= pageCount)
    .sort((first, second) => first - second);
  const items = [];

  pages.forEach((page, index) => {
    const previousPage = pages[index - 1];
    if (previousPage && page - previousPage > 1) {
      items.push(`ellipsis-${previousPage}`);
    }
    items.push(page);
  });

  return items;
};

function UserDetails({
  userId,
  initialUser,
  departments = [],
  onClose,
  onEdit,
  onResetPassword,
}) {
  const id = userId;
  const suppliedUser = initialUser;
  const [user, setUser] = useState(suppliedUser || null);
  const [activeTab, setActiveTab] = useState("overview");
  const [isLoading, setIsLoading] = useState(!suppliedUser);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showForceLogoutConfirm, setShowForceLogoutConfirm] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [loginEvents, setLoginEvents] = useState([]);
  const [loginHistoryPage, setLoginHistoryPage] = useState(1);
  const [loginHistoryTotal, setLoginHistoryTotal] = useState(0);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);
  const loginHistoryCount = 5;

  useEffect(() => {
    let isMounted = true;

    const loadUser = async () => {
      try {
        const [detailsResponse, statusResponse] = await Promise.all([
          fetchUserDetails(id),
          fetchUserOnlineStatus(),
        ]);
        if (!isMounted) return;
        const details =
          detailsResponse?.item || detailsResponse?.user || detailsResponse;
        const statusItems =
          statusResponse?.items ||
          statusResponse?.users ||
          (Array.isArray(statusResponse) ? statusResponse : []);
        const status = statusItems.find(
          (item) => String(item.user_id || item.id) === String(id),
        );
        if (details && !details.description) {
          setUser((current) => ({ ...current, ...details, ...status }));
        } else if (status) {
          setUser((current) => ({ ...current, ...status }));
        }
      } catch (error) {
        if (!suppliedUser) displayToast("Failed to load user details", "error");
        console.error(error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadUser();
    return () => {
      isMounted = false;
    };
  }, [id, suppliedUser]);

  useEffect(() => {
    if (activeTab !== "sessions" || sessionsLoaded) return;

    let isMounted = true;
    const loadSessions = async () => {
      try {
        setSessionsLoading(true);
        const response = await fetchUserSessions(id, "?page=1&count=100");
        if (!isMounted) return;
        if (response?.description && !response?.items) {
          displayToast(response.description, "error");
          return;
        }
        setSessions(
          response?.items || (Array.isArray(response) ? response : []),
        );
        setSessionsLoaded(true);
      } catch (error) {
        if (isMounted) displayToast("Failed to load user sessions", "error");
        console.error(error);
      } finally {
        if (isMounted) setSessionsLoading(false);
      }
    };

    loadSessions();
    return () => {
      isMounted = false;
    };
  }, [activeTab, id, sessionsLoaded]);

  useEffect(() => {
    if (activeTab !== "login") return;

    let isMounted = true;
    const loadLoginHistory = async () => {
      try {
        setLoginHistoryLoading(true);
        const response = await fetchUserLoginHistory(
          id,
          `?page=${loginHistoryPage}&count=${loginHistoryCount}`,
        );
        if (!isMounted) return;
        if (response?.description && !response?.items) {
          displayToast(response.description, "error");
          return;
        }
        const items =
          response?.items || (Array.isArray(response) ? response : []);
        setLoginEvents(items);
        setLoginHistoryTotal(response?.total_count ?? items.length);
      } catch (error) {
        if (isMounted) displayToast("Failed to load login history", "error");
        console.error(error);
      } finally {
        if (isMounted) setLoginHistoryLoading(false);
      }
    };

    loadLoginHistory();
    return () => {
      isMounted = false;
    };
  }, [activeTab, id, loginHistoryPage]);

  const handleForceLogout = async () => {
    try {
      setIsLoggingOut(true);
      const response = await forceLogoutUser(id);
      if (response?.description && response?.status === "error") {
        displayToast(response.description, "error");
        return;
      }
      setUser((current) => ({
        ...current,
        online_status: "Offline",
        active_session_count: 0,
      }));
      displayToast(
        `${response?.terminated_sessions ?? 0} session(s) terminated`,
        "success",
      );
      setShowForceLogoutConfirm(false);
    } catch (error) {
      displayToast("Failed to force logout user", "error");
      console.error(error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (isLoading) {
    return <div className="user-details-state">Loading user details...</div>;
  }

  if (!user) {
    return (
      <div className="user-details-state">
        <p>User details could not be found.</p>
        <button className="mfg-btn mfg-btn-outline" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  const role = user.user_role?.role || user.role || "N/A";
  const departmentId =
    user.department_id ||
    user.department?.id ||
    user.user_role?.department_id ||
    user.role?.department_id;
  const department =
    (typeof user.department === "string"
      ? user.department
      : user.department?.name) ||
    user.department_name ||
    user.user_role?.department ||
    departments.find((item) => String(item.id) === String(departmentId))
      ?.name ||
    "N/A";
  const accountStatus = user.status || user.account_status || "N/A";
  const presenceStatus =
    user.online_status === true
      ? "ONLINE"
      : String(user.online_status || "OFFLINE").toUpperCase();
  const isOnline = presenceStatus === "ONLINE";
  const isIdle = presenceStatus === "IDLE";
  const presenceLabel = isOnline ? "Online" : isIdle ? "Idle" : "Offline";
  const presenceDescription = isOnline
    ? "Active now"
    : isIdle
      ? "Away"
      : "Not active";
  const transactions = getList(user, ["transaction_activity", "transactions"]);
  const loginHistoryPageCount = Math.ceil(
    loginHistoryTotal / loginHistoryCount,
  );

  const getLoginEventClass = (eventType = "") => {
    const normalized = eventType.toLowerCase();
    if (normalized.includes("success") || normalized === "login")
      return "success";
    if (normalized.includes("timeout")) return "warning";
    return "danger";
  };

  const formatEventName = (eventType = "") => {
    const normalized = eventType.toUpperCase();
    if (normalized === "LOGOUT" || normalized === "LOGGED_OUT") {
      return "Logout";
    }
    if (normalized === "FORCED_LOGOUT") return "Forced Logout";
    return (
      eventType
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase()) || "Activity"
    );
  };

  const renderActivityList = (items, emptyText, icon) =>
    items.length ? (
      <div className="user-details-activity-list">
        {items.map((item, index) => (
          <div className="user-details-activity-row" key={item.id || index}>
            {icon}
            <div>
              <strong>
                {item.title ||
                  item.device ||
                  item.action ||
                  item.status ||
                  "Activity"}
              </strong>
              <span>
                {formatDate(
                  item.created_at ||
                    item.last_activity_at ||
                    item.login_at ||
                    item.timestamp,
                )}
              </span>
            </div>
            <small>{item.ip_address || item.reference || ""}</small>
          </div>
        ))}
      </div>
    ) : (
      <div className="user-details-empty">{emptyText}</div>
    );

  return (
    <div className="mfg-page-container user-details-page user-details-drawer-content">
      <div className="user-details-toolbar">
        <button
          className="user-details-back"
          onClick={onClose}
          title="Close user details"
        >
          <X size={19} />
        </button>
        <h2>
          <User size={21} /> User Details
        </h2>
      </div>

      <section className="user-details-profile">
        <div className="user-details-avatar">{getInitials(user.user_name)}</div>
        <div className="user-details-profile-copy">
          <h1>{user.user_name || "Unnamed user"}</h1>
          <span
            className={`user-details-account ${String(accountStatus).toLowerCase() === "active" ? "active" : "inactive"}`}
          >
            {accountStatus} Account
          </span>
          <p>{user.email || "No email available"}</p>
        </div>
        <div className="user-details-meta">
          <div>
            <span>Role</span>
            <strong>{role}</strong>
          </div>
          <div>
            <span>Department</span>
            <strong>{department}</strong>
          </div>
          <div>
            <span>User ID</span>
            <strong>{user.id || id}</strong>
          </div>
        </div>
      </section>

      <section className="user-details-metrics">
        <div
          className={`user-details-metric ${isOnline ? "online" : isIdle ? "idle" : "offline"}`}
        >
          <Activity />
          <span>
            <strong>{presenceLabel}</strong>
            {presenceDescription}
          </span>
        </div>
        <div className="user-details-metric">
          <Laptop />
          <span>
            <small>Active Sessions</small>
            <strong>{user.active_session_count ?? user.sessions ?? 0}</strong>
          </span>
        </div>
        <div className="user-details-metric">
          <Clock3 />
          <span>
            <small>Last Activity</small>
            <strong>
              {formatDate(user.last_activity_at || user.last_active)}
            </strong>
          </span>
        </div>
        <div className="user-details-metric">
          <History />
          <span>
            <small>Last Login</small>
            <strong>
              {formatLastLogin(
                user.last_login_at ||
                  user.last_login ||
                  user.last_activity_at ||
                  user.last_active,
              )}
            </strong>
          </span>
        </div>
      </section>

      <nav className="user-details-tabs" aria-label="User detail sections">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={activeTab === tab.id ? "active" : ""}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === "overview" && (
        <div className="user-details-overview">
          <section className="user-details-panel">
            <div className="user-details-panel-heading">
              <h3>
                <Users size={18} /> User Information
              </h3>
              {onEdit && (
                <button
                  className="user-details-edit-button"
                  onClick={() => onEdit(user)}
                >
                  <Edit2 size={15} /> Edit
                </button>
              )}
            </div>
            <div className="user-details-info-grid">
              <div>
                <span>Full Name</span>
                <strong>{user.full_name || user.user_name || "N/A"}</strong>
              </div>
              <div>
                <span>Email</span>
                <strong>{user.email || "N/A"}</strong>
              </div>
              <div>
                <span>Department</span>
                <strong>{department}</strong>
              </div>
              <div>
                <span>Role</span>
                <strong>{role}</strong>
              </div>
              <div>
                <span>Account Status</span>
                <strong>{accountStatus}</strong>
              </div>
              <div>
                <span>Created At</span>
                <strong>
                  {formatDate(user.created_at || user.user_role?.created_at)}
                </strong>
              </div>
            </div>
            {onResetPassword && (
              <button
                className="user-details-reset-button"
                onClick={() => onResetPassword(user)}
              >
                <KeyRound size={16} /> Reset Password
              </button>
            )}
          </section>
          <section className="user-details-panel">
            <h3>
              <CalendarDays size={18} /> Recent Activity
            </h3>
            <div className="user-details-recent">
              <span>Last Activity</span>
              <strong>
                {formatDate(user.last_activity_at || user.last_active)}
              </strong>
            </div>
            <div className="user-details-recent">
              <span>Last Login</span>
              <strong>
                {formatLastLogin(
                  user.last_login_at ||
                    user.last_login ||
                    user.last_activity_at ||
                    user.last_active,
                )}
              </strong>
            </div>
          </section>
          <section className="user-details-panel user-details-quick-actions">
            <h3>
              <LogOut size={18} /> Quick Actions
            </h3>
            <button
              onClick={() => setShowForceLogoutConfirm(true)}
              disabled={isLoggingOut}
            >
              <LogOut size={17} />
              {isLoggingOut ? "Logging out..." : "Force Logout (All Sessions)"}
            </button>
            <p>This will terminate all active sessions for this user.</p>
          </section>
        </div>
      )}

      {activeTab === "sessions" && (
        <section className="user-details-sessions-section">
          <h3>Current Sessions ({sessions.length})</h3>
          {sessionsLoading ? (
            <div className="user-details-empty">Loading sessions...</div>
          ) : sessions.length ? (
            <div className="user-details-session-list">
              {sessions.map((session, index) => {
                const isActive =
                  String(session.status).toLowerCase() === "active";
                return (
                  <article
                    className="user-details-session-card"
                    key={session.session_id || index}
                  >
                    <div className="user-details-session-header">
                      <span
                        className={`user-details-session-status ${isActive ? "active" : "inactive"}`}
                      >
                        <span />{" "}
                        {isActive
                          ? "Active Session"
                          : session.status || "Inactive"}
                      </span>
                      <strong>
                        <Laptop size={16} />
                        {[
                          session.device_type,
                          session.browser,
                          session.operating_system,
                        ]
                          .filter(Boolean)
                          .join(" • ") || "Unknown device"}
                      </strong>
                    </div>
                    <div className="user-details-session-grid">
                      <div>
                        <span>IP Address</span>
                        <strong>{session.ip_address || "N/A"}</strong>
                      </div>
                      <div>
                        <span>Login Time</span>
                        <strong>{formatDate(session.login_at)}</strong>
                      </div>
                      <div>
                        <span>Last Activity</span>
                        <strong>{formatDate(session.last_activity_at)}</strong>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="user-details-panel user-details-empty">
              No active sessions found.
            </div>
          )}
        </section>
      )}
      {activeTab === "login" && (
        <section className="user-details-login-section">
          {loginHistoryLoading ? (
            <div className="user-details-panel user-details-empty">
              Loading login history...
            </div>
          ) : loginEvents.length ? (
            <>
              <div className="user-details-login-table-wrap">
                <table className="user-details-login-table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Date &amp; Time</th>
                      <th>Device</th>
                      <th>IP Address</th>
                      <th>Performed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginEvents.map((event, index) => (
                      <tr
                        key={
                          event.id ||
                          `${event.session_id || "event"}-${event.event_time || event.timestamp}-${index}`
                        }
                      >
                        <td>
                          <span
                            className={`user-details-login-event ${getLoginEventClass(event.event_type)}`}
                          >
                            <span /> {formatEventName(event.event_type)}
                          </span>
                        </td>
                        <td>
                          {formatDate(event.event_time || event.timestamp)}
                        </td>
                        <td>
                          {event.device ||
                            [
                              event.device_type,
                              event.browser,
                              event.operating_system,
                            ]
                              .filter(Boolean)
                              .join(" / ") ||
                            "N/A"}
                        </td>
                        <td>{event.ip || event.ip_address || "N/A"}</td>
                        <td>
                          {event.performed_by ||
                            (event.performed_by_user_id
                              ? `Admin (ID: ${event.performed_by_user_id})`
                              : "-")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="user-details-login-footer">
                <span>
                  Showing {(loginHistoryPage - 1) * loginHistoryCount + 1} to{" "}
                  {Math.min(
                    loginHistoryPage * loginHistoryCount,
                    loginHistoryTotal,
                  )}{" "}
                  of {loginHistoryTotal} events
                </span>
                {loginHistoryPageCount > 1 && (
                  <div className="user-details-login-pagination">
                    <button
                      onClick={() => setLoginHistoryPage((page) => page - 1)}
                      disabled={loginHistoryPage === 1}
                      aria-label="Previous login history page"
                    >
                      &lsaquo;
                    </button>
                    {getPaginationItems(
                      loginHistoryPageCount,
                      loginHistoryPage,
                    ).map((item) =>
                      typeof item === "string" ? (
                        <span
                          className="user-details-pagination-ellipsis"
                          key={item}
                          aria-hidden="true"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={item}
                          className={item === loginHistoryPage ? "active" : ""}
                          onClick={() => setLoginHistoryPage(item)}
                          aria-label={`Login history page ${item}`}
                        >
                          {item}
                        </button>
                      ),
                    )}
                    <button
                      onClick={() => setLoginHistoryPage((page) => page + 1)}
                      disabled={loginHistoryPage === loginHistoryPageCount}
                      aria-label="Next login history page"
                    >
                      &rsaquo;
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="user-details-panel user-details-empty">
              No login history found.
            </div>
          )}
        </section>
      )}
      {activeTab === "transactions" && (
        <section className="user-details-panel">
          {renderActivityList(
            transactions,
            "No transaction activity found.",
            <Activity size={18} />,
          )}
        </section>
      )}

      {showForceLogoutConfirm && (
        <div
          className="user-details-confirm-overlay"
          onClick={() => !isLoggingOut && setShowForceLogoutConfirm(false)}
        >
          <div
            className="user-details-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="force-logout-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="user-details-confirm-icon">
              <AlertTriangle size={24} />
            </div>
            <h3 id="force-logout-title">Force logout all sessions?</h3>
            <p>
              Do you want to log out <strong>{user.user_name}</strong> from
              every active session? They will need to sign in again on all
              devices.
            </p>
            <div className="user-details-confirm-actions">
              <button
                type="button"
                className="cancel"
                disabled={isLoggingOut}
                onClick={() => setShowForceLogoutConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm"
                disabled={isLoggingOut}
                onClick={handleForceLogout}
              >
                <LogOut size={16} />
                {isLoggingOut ? "Logging out..." : "Force Logout"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetails;
